import { SkyCommonNavV2 } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { BrandLogo } from "@/components/shell/BrandLogo";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useAuth } from "@/lib/auth/AuthContext";
import { APP_SWITCHER_ACCESS, SKY_ENVIRONMENT } from "@/lib/env";
import { FOCUS_RING } from "@/lib/layout";
import { logger } from "@/lib/logger";
import { InstanceSwitcher } from "./InstanceSwitcher";
import { TopbarUserProfile } from "./TopbarUserProfile";

/**
 * The global top bar (UX-1, GUIDELINES.md §3.4): full-width, fixed, 48px tall,
 * always the same dark canvas regardless of theme — it is brand chrome, not a
 * themed surface, which is why it paints `bg-topbar` rather than `bg-card`.
 *
 * Right-hand side: the Skypoint app switcher, the instance switcher, then the
 * user menu (which carries the theme control and sign-out).
 */
export function TopBar() {
  const { getAccessToken } = useAuth();
  const { selectedInstance } = useTenantInstance();

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 flex h-12 items-center justify-between bg-topbar pl-5 pr-4 text-topbar-foreground"
      role="banner"
      data-testid="top-bar"
    >
      {/* The skyData lockup, once on the screen (owner, 2026-09-30; never a second mark in the
          nav rail). Inline: the coins beside the wordmark, 32px of the bar's 48, because the
          stacked lockup's wordmark would be unreadable at that height. The wordmark draws in
          currentColor, the bar's white here. The link names itself with `aria-label`, so the
          picture inside is decorative and nothing is announced twice. */}
      <Link
        href="/"
        aria-label="skyData home"
        className={`flex shrink-0 items-center rounded-sm ${FOCUS_RING}`}
      >
        <BrandLogo layout="inline" lockupClassName="h-8" decorative />
      </Link>

      {/* `min-w-0` down this chain lets the instance name truncate at phone width, so the app
          switcher and the user menu stay on screen instead of being pushed past the edge. */}
      <div className="flex min-w-0 items-center gap-3">
        <InstanceSwitcher />
        <div className="h-6 w-px bg-topbar-foreground/25" aria-hidden="true" />
        {/* The Skypoint app switcher. With `APP_SWITCHER_ACCESS` it is access-aware:
            `getToken` hands it the same bearer our own API gets and skySupport identifies the
            user from the token's `oid`; failures are reported rather than swallowed. Without it
            (the default until skySupport allows the skyData origins, docs/backlog.md) it is the
            library's classic list and makes no request, so no page logs a CORS error. */}
        <SkyCommonNavV2
          currentApp="skydata"
          environment={SKY_ENVIRONMENT}
          instanceId={selectedInstance?.id}
          {...(APP_SWITCHER_ACCESS
            ? {
                getToken: getAccessToken,
                prefetchAccess: true,
                onAccessError: (error: unknown) =>
                  logger.warn("app switcher access lookup failed", {
                    "skydata.error": String(error),
                  }),
              }
            : {})}
          triggerClassName="text-white!"
          // The company is "Skypoint" in user-visible copy; the kit's default
          // label spells it "SkyPoint". Its other strings need a kit change.
          triggerLabel="Switch Skypoint app"
        />
        <div className="h-6 w-px bg-topbar-foreground/25" aria-hidden="true" />
        <TopbarUserProfile />
      </div>
    </header>
  );
}
